import type { FC } from "react";
import React, { useCallback, useContext, useMemo } from "react";

import { useUserDomains } from "../hooks/accounts/useUserDomains";
import type { UserDomainData } from "../utils/name-service";

export interface DomainsContextState {
  userDomains: UserDomainData[];
  refreshDomains: () => void;
  refreshing: boolean;
}

const DomainsContext = React.createContext<DomainsContextState>({
  userDomains: [],
  refreshing: false,
  refreshDomains: () => {},
});

export const DomainsProvider: FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { isUserDomainsRefreshing, refreshUserDomains, userDomains } = useUserDomains();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedUserDomains = useMemo(() => userDomains, [userDomains.length]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedRefreshUserDomains = useCallback(refreshUserDomains, []);

  const memoizedContextValue: DomainsContextState = useMemo(
    () => ({
      userDomains: memoizedUserDomains,
      refreshing: isUserDomainsRefreshing,
      refreshDomains: memoizedRefreshUserDomains,
    }),
    [memoizedUserDomains, memoizedRefreshUserDomains, isUserDomainsRefreshing],
  );

  return <DomainsContext.Provider value={memoizedContextValue}>{children}</DomainsContext.Provider>;
};

export const useDomainsContext = () => {
  return useContext(DomainsContext);
};
